// Auth helpers — relies on cookies set by accounts.prototypical.ai.
// The portal-api validates and returns the user via /api/sites/me.

import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Me } from "./api";
import { subscribeToSiteChanges } from "./siteEvents";

type AuthState = { status: "loading" } | { status: "authed"; me: Me } | { status: "anon" } | { status: "error"; retry: () => void };

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((current) => current + 1), []);
  useEffect(() => {
    let cancelled = false;
    let requestId = 0;
    let pendingBuilds = false;
    let timer: number | undefined;
    setState({ status: "loading" });
    const load = async (background = false) => {
      window.clearTimeout(timer);
      const currentRequest = ++requestId;
      try {
        const me = await api.me();
        if (!cancelled && currentRequest === requestId) {
          pendingBuilds = me.sites.some(site => !!site.pendingBuildId);
          setState({ status: "authed", me });
        }
      } catch (reason) {
        if (!cancelled && currentRequest === requestId) {
          if (reason instanceof ApiError && reason.status === 401) pendingBuilds = false;
          // A network/server failure does not mean the client signed out.
          // Initial verification must succeed; background refreshes keep a verified view mounted.
          setState(current => reason instanceof ApiError && reason.status === 401
            ? { status: "anon" }
            : background && current.status === "authed" ? current : { status: "error", retry });
        }
      } finally {
        // The persistent shell must notice completion even on pages without a
        // site/job poller. /me reconciles pending builds before returning.
        if (!cancelled && currentRequest === requestId && pendingBuilds) {
          timer = window.setTimeout(() => { void load(true); }, 5_000);
        }
      }
    };
    void load();
    const unsubscribe = subscribeToSiteChanges(() => { void load(true); });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, [attempt, retry]);
  return state;
}
